

class MetadataService {
  constructor({ axiosInstance }) {
    this.axiosInstance = axiosInstance;
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  async fetchWithCache(key, fetchFn) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    const data = await fetchFn();
    this.cache.set(key, { data, timestamp: Date.now() });
    return data;
  }

  async getObjects() {
    return this.fetchWithCache('objects', async () => {
      const response = await this.axiosInstance.get('/objects');
      return this.transformObjectsResponse(response.data);
    });
  }

  async getObjectSchema(objectType) {
    return this.fetchWithCache(`schema_${objectType}`, async () => {
      const response = await this.axiosInstance.get(`/objects/${objectType}/schema`);
      return response.data;
    });
  }

  async getFields(objectType) {
    return this.fetchWithCache(`fields_${objectType}`, async () => {
      const response = await this.axiosInstance.get(`/fields/${objectType}`);
      return this.transformFieldsResponse(response.data);
    });
  }

  async getRelationships(objectType) {
    return this.fetchWithCache(`relationships_${objectType}`, async () => {
      const response = await this.axiosInstance.get(`/related-objects/${objectType}`);
      return response.data;
    });
  }

  async getOperators(fieldType) {
    return this.fetchWithCache(`operators_${fieldType}`, async () => {
      const response = await this.axiosInstance.get(`/operators/${fieldType}`);
      return response.data;
    });
  }

  async getReferenceData(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    try {
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error(`Error fetching reference data from ${url}:`, error);
      return [];
    }
  }

  transformObjectsResponse(objects) {
    // Don't transform if already in correct format
    if (objects && objects.length > 0 && objects[0].hasOwnProperty('type')) {
      // Already transformed by backend, just ensure all properties exist
      return objects.map(obj => ({
        ...obj,
        icon: obj.icon || this.getDefaultIcon(obj.id),
        capabilities: obj.capabilities || this.getDefaultCapabilities(obj.id),
        metadata: obj.metadata || {}
      }));
    }
    
    // Old transform logic for backward compatibility
    return objects.map(obj => ({
      id: typeof obj === 'string' ? obj : obj.id,
      name: typeof obj === 'string'
        ? obj.charAt(0).toUpperCase() + obj.slice(1)
        : obj.name || obj.id,
      type: obj.id?.startsWith('co_') ? 'custom' : 'standard',
      icon: obj.icon || this.getDefaultIcon(obj.id || obj),
      capabilities: obj.capabilities || this.getDefaultCapabilities(obj.id || obj),
      metadata: obj.metadata || {}
    }));
  }

  transformFieldsResponse(fields) {
    return Object.entries(fields).map(([fieldId, fieldDef]) => ({
      id: fieldId,
      label: fieldDef.label || this.formatLabel(fieldId),
      type: fieldDef.type,
      required: fieldDef.required || false,
      options: fieldDef.options || [],
      metadata: {
        auto_generated: fieldDef.auto_generated || false,
        is_custom: fieldDef.is_custom || false,
        reference_to: fieldDef.reference_to,
        validation: fieldDef.validation || {}
      }
    }));
  }

  getDefaultIcon(objectType) {
    const iconMap = {
      account: 'account_circle',
      contact: 'person',
      lead: 'leaderboard',
      deal: 'handshake',
      case: 'support',
      order: 'shopping_cart',
      invoice: 'receipt',
      quotation: 'request_quote',
      product: 'inventory'
    };
    return iconMap[objectType] || 'folder';
  }

  getDefaultCapabilities() {
    return {
      triggers: ['create', 'update', 'delete'],
      actions: ['create', 'update', 'delete'],
      features: []
    };
  }

  formatLabel(fieldId) {
    return fieldId
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  clearCache() {
    this.cache.clear();
  }

  clearCacheForObject(objectType) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.includes(objectType)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

export default MetadataService;
